import React, { useRef } from "react";
import './PolicyStyles.css';
import { exportToWord } from '../utils/exportToWord';

const DataProtectionPolicy = ({
  clientName,
  clientAddress,
  effectiveDate,
  companyNumber,
  dataProtectionOfficerContact,
  supervisoryAuthorityContact,
}) => {
  const contentRef = useRef();

  const handleExportToWord = () => {
    if (contentRef.current) {
      exportToWord(contentRef.current.innerHTML, "Data_Protection_Policy_GDPR.docx");
    }
  };

  return (
    <div className="policy-container">
      <div ref={contentRef} id="policy-content">
      <h3 className="policy-title">Accident & Incident Management Policy</h3>
      <p className="policy-details"><strong>Company Name:</strong> {clientName || "[Insert Company Name]"}</p>
      <p className="policy-details"><strong>Company Number:</strong> {companyNumber || "[Insert Company Number]"}</p>
      <p className="policy-details"><strong>Company Address:</strong> {clientAddress || "[Insert Company Address]"}</p>
      <p className="policy-details"><strong>Policy Effective Date:</strong> {effectiveDate || "[Insert Effective Date]"}</p>

        <h4 className="section-title">1. Introduction</h4>
        <p className="section-content">
          {clientName || "The Company"} is committed to protecting the privacy and security of personal data in accordance with the General Data Protection Regulation (GDPR) (EU) 2016/679, the UK Data Protection Act 2018, and all relevant data protection legislation. This policy sets out how we collect, store, process, and protect personal data and the rights of individuals regarding their data.
        </p>

        <h4 className="section-title">2. Scope</h4>
        <p className="section-content">
          This policy applies to all personal data processed by {clientName || "the Company"}, whether related to employees, customers, suppliers, contractors, or any other data subjects. It covers all formats including paper records, electronic files, and verbal information. All employees, contractors, and third parties working with {clientName || "the Company"} must comply with this policy.
        </p>

        <h4 className="section-title">3. Definitions</h4>
        <ul>
          <li><strong>Personal Data:</strong> Any information relating to an identified or identifiable natural person.</li>
          <li><strong>Data Subject:</strong> The individual whose personal data is processed.</li>
          <li><strong>Processing:</strong> Any operation performed on personal data, including collection, storage, use, disclosure, or destruction.</li>
          <li><strong>Data Controller:</strong> The entity which determines the purposes and means of processing personal data. In this context, {clientName || "the Company"} is the Data Controller.</li>
          <li><strong>Data Processor:</strong> A person or organisation which processes personal data on behalf of the Data Controller.</li>
        </ul>

        <h4 className="section-title">4. Data Protection Principles</h4>
        <p className="section-content">
          {clientName || "The Company"} commits to processing personal data lawfully, fairly, and transparently, in line with the following GDPR principles:
        </p>
        <ul>
          <li>Lawfulness, fairness, and transparency.</li>
          <li>Purpose limitation: collected for specified, explicit, and legitimate purposes only.</li>
          <li>Data minimisation: adequate, relevant, and limited to what is necessary.</li>
          <li>Accuracy: kept up to date and corrected where necessary.</li>
          <li>Storage limitation: retained only as long as necessary.</li>
          <li>Integrity and confidentiality: processed securely to prevent unauthorised or unlawful processing, accidental loss, destruction, or damage.</li>
          <li>Accountability: responsible and able to demonstrate compliance with these principles.</li>
        </ul>

        <h4 className="section-title">5. Lawful Basis for Processing</h4>
        <p className="section-content">
          Personal data will only be processed where there is a lawful basis including:
        </p>
        <ul>
          <li>Consent: where the individual has given clear consent for processing.</li>
          <li>Contractual necessity: to perform a contract with the individual.</li>
          <li>Legal obligation: to comply with a legal duty.</li>
          <li>Vital interests: to protect someone’s life.</li>
          <li>Public task: to perform a task in the public interest or official authority.</li>
          <li>Legitimate interests: where processing is necessary for legitimate interests not overridden by individual rights.</li>
        </ul>

        <h4 className="section-title">6. Rights of Data Subjects</h4>
        <p className="section-content">Individuals have the following rights in relation to their personal data:</p>
        <ul>
          <li>The right to be informed about how their data is used.</li>
          <li>The right to access their personal data.</li>
          <li>The right to rectification of inaccurate data.</li>
          <li>The right to erasure (‘right to be forgotten’), in certain circumstances.</li>
          <li>The right to restrict processing.</li>
          <li>The right to data portability.</li>
          <li>The right to object to processing.</li>
          <li>Rights related to automated decision making and profiling.</li>
        </ul>
        <p className="section-content">
          <strong>How to Exercise Your Rights:</strong> To exercise any of these rights, please contact our Data Protection Officer at {dataProtectionOfficerContact || "[Insert DPO Contact]"}. We will respond to all requests in accordance with GDPR requirements.
        </p>

        <h4 className="section-title">7. Data Security</h4>
        <p className="section-content">
          Appropriate technical and organisational measures will be taken to safeguard personal data including:
        </p>
        <ul>
          <li>Access controls and user authentication.</li>
          <li>Data encryption where appropriate.</li>
          <li>Secure storage of paper and electronic records.</li>
          <li>Regular security training for employees.</li>
          <li>Procedures for detecting, reporting, and investigating personal data breaches.</li>
        </ul>

        <h4 className="section-title">8. Data Retention</h4>
        <p className="section-content">
          Personal data will be retained only as long as necessary for the purposes collected, or to comply with legal or regulatory requirements. Retention periods will be documented and reviewed regularly.
        </p>

        <h4 className="section-title">9. Data Sharing and Transfers</h4>
        <p className="section-content">
          Personal data will not be shared with third parties except where necessary for the performance of contracts, legal obligations, or with explicit consent. International transfers of data will comply with GDPR requirements, using mechanisms such as adequacy decisions or Standard Contractual Clauses to ensure adequate protection.
        </p>

        <h4 className="section-title">10. Data Breach Notification</h4>
        <p className="section-content">
          In the event of a personal data breach, {clientName || "the Company"} will comply with GDPR obligations to notify the Information Commissioner’s Office (ICO) within 72 hours where feasible and notify affected data subjects if there is a high risk to their rights and freedoms.
        </p>

        <h4 className="section-title">11. Roles and Responsibilities</h4>
        <p className="section-content">
          <strong>Data Protection Officer (DPO):</strong> {clientName || "The Company"} {dataProtectionOfficerContact ? "has appointed" : "will appoint, if required,"} a Data Protection Officer responsible for monitoring compliance, training, and acting as the contact point for data subjects and the ICO.
        </p>
        <p className="policy-details"><strong>DPO Contact:</strong> {dataProtectionOfficerContact || "[Insert DPO Contact]"}</p>

        <h4 className="section-title">12. Supervisory Authority Contact</h4>
        <p className="section-content">
          If you have concerns about how your data is handled, you may contact our Data Protection Officer or the relevant Supervisory Authority:
        </p>
        <p className="policy-details">
          <strong>Supervisory Authority Contact:</strong> {supervisoryAuthorityContact || "Information Commissioner’s Office (ICO): https://ico.org.uk/make-a-complaint/"}
        </p>

        <h4 className="section-title">13. Training and Awareness</h4>
        <p className="section-content">
          All employees, contractors, and relevant third parties will receive appropriate data protection training and awareness to understand their obligations under GDPR and this policy.
        </p>

        <h4 className="section-title">14. Policy Review</h4>
        <p className="section-content">
          This policy will be reviewed at least annually or when legislative changes occur to ensure continued compliance.
        </p>

        <h4 className="section-title">Employee Acknowledgment and Signature</h4>
        <p>
          I acknowledge that I have read and understood the Data Protection Policy and agree to comply with its requirements.
        </p>
        <p>Employee Name: _________________________</p>
        <p>Employee Signature: _________________________</p>
        <p>Date: _________________________</p>

        <h4 className="section-title">Management Approval and Signature</h4>
        <p>This policy has been approved by senior management and is hereby in effect.</p>
        <p>Manager/Officer Name: _________________________</p>
        <p>Title: _________________________</p>
        <p>Signature: _________________________</p>
        <p>Date: _________________________</p>

              <h4 className="section-title">Data Protection Officer Signature</h4>
        <p>This policy has been approved by The appointed Data Protection Officer and is hereby in effect.</p>
        <p>Data Protection Officer Name: _________________________</p>
        <p>Title: _________________________</p>
        <p>Signature: _________________________</p>
        <p>Date: _________________________</p>
      </div>

      {/* Export to Word Button */}
      <div className="mt-4 text-center">
        <button
          onClick={handleExportToWord}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition"
        >
          Export to Word
        </button>
      </div>
    </div>
  );
};

export default DataProtectionPolicy;